import axios from 'axios';
import moment from 'moment';
import { ContentType } from '../constants/mediaFormatProfiles';
import {
  TIMEOUT_API_SHORT,
  TIMEOUT_CHUNK_UPLOAD,
  TIMEOUT_FINALIZE,
  TIMEOUT_PULSE,
} from '../constants/uploadTimeouts';
import { IVFSResponse } from '../services/fileSystemService';
import { apiV2 } from '../lib/baseAxios';
import { FileType, TimeStampedLine } from '../types';
import logger from '../lib/logger';
import { getToken } from '../lib/localStorageHandler';

export type MultipartAbortReasonCode =
  | 'user_cancelled_file_upload'
  | 'browser_closed_during_file_upload'
  | 'user_cancelled_recorder_upload'
  | 'browser_closed_during_recorder_upload';

export interface MultipartAbortReason {
  reason?: string;
  reasonCode?: MultipartAbortReasonCode | string;
  reasonDetail?: string;
}

interface InitializeMultipartUploadOptions {
  teamId: string;
  folderId: string;
  contentType: ContentType;
  source?: string;
}
interface InitializeMultipartUploadResponse {
  fileId: string;
  uploadId: string;
}
export const initializeMultipartUpload = async ({
  teamId,
  folderId,
  contentType,
  source,
}: InitializeMultipartUploadOptions) => {
  // /:teamId/:folderId/upload/multipart
  const response = await apiV2.put<
    IVFSResponse<InitializeMultipartUploadResponse>
  >(
    `/files/upload/multipart/init/${teamId}/${folderId}`,
    { contentType, source },
    { timeout: TIMEOUT_API_SHORT },
  );
  return response.data.data;
};

interface CreatePartUploadUrl {
  teamId: string;
  folderId: string;
  fileId: string;
  uploadId: string;
  partNumber: number;
  contentType: ContentType;
}

interface PartUploadUrlResponse {
  uploadUrl: string;
}

export const createPartUploadUrl = async ({
  teamId,
  folderId,
  fileId,
  uploadId,
  partNumber,
  contentType,
}: CreatePartUploadUrl) => {
  // /:teamId/:folderId/upload/multipart/part
  const response = await apiV2.put<IVFSResponse<PartUploadUrlResponse>>(
    `/files/upload/multipart/url/${teamId}/${folderId}/${fileId}/${uploadId}/${partNumber}`,
    { contentType },
    { timeout: TIMEOUT_API_SHORT },
  );
  return response.data.data.uploadUrl;
};

type FinalizeUploadOptions = {
  teamId: string;
  folderId?: string;
  fileId: string;
  uploadId: string;
  contentType: ContentType;
  notes: TimeStampedLine[];
  title?: string;
  needsConversion?: boolean;
};
interface FinalizeUploadResponseBody {
  file: FileType;
}

export const finalizeUpload = async ({
  teamId,
  folderId,
  fileId,
  uploadId,
  contentType,
  notes,
  title,
  needsConversion,
}: FinalizeUploadOptions) => {
  // /:teamId/:folderId/upload/multipart
  const response = await apiV2.put<IVFSResponse<FinalizeUploadResponseBody>>(
    `/files/upload/multipart/finalize/${teamId}/${folderId}/${fileId}/${uploadId}`,
    {
      file: {
        contentType,
        name:
          title?.trim() ||
          `New Recording ${moment().format('h:mma MMM DD YYYY')}`,
        notes,
        needsConversion,
      },
    },
    { timeout: TIMEOUT_FINALIZE },
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Upload finalization failed');
  }

  return response.data.data.file;
};

export const uploadChunkToS3 = async ({
  uploadUrl,
  chunk,
  signal,
}: {
  uploadUrl: string;
  chunk: Blob;
  signal?: AbortSignal;
}) => {
  if (!uploadUrl) {
    throw new Error('No upload URL provided');
  }
  try {
    await axios.put(uploadUrl, chunk, {
      headers: {
        'Content-Type': chunk.type,
      },
      signal,
      timeout: TIMEOUT_CHUNK_UPLOAD,
    });
  } catch (error) {
    logger.error('Error uploading chunk to S3', error);
    throw error;
  }
};

/**
 * Upload chunk directly to backend fallback endpoint when S3 upload fails
 */
export const uploadChunkToFallback = async ({
  teamId,
  folderId,
  fileId,
  uploadId,
  partNumber,
  chunk,
  contentType,
  signal,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
  uploadId: string;
  partNumber: number;
  chunk: Blob;
  contentType: ContentType;
  signal?: AbortSignal;
}) => {
  try {
    logger.info(
      `Using fallback upload for part ${partNumber}, fileId: ${fileId}`,
    );

    // Create FormData to send the chunk as multipart form data
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('contentType', contentType);

    const response = await apiV2.put(
      `/files/upload/multipart/fallback/${teamId}/${folderId}/${fileId}/${uploadId}/${partNumber}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        signal,
        timeout: TIMEOUT_CHUNK_UPLOAD,
      },
    );

    logger.info(`Successfully uploaded part ${partNumber} using fallback`);
    return response.data;
  } catch (error) {
    logger.error('Error uploading chunk to fallback endpoint', error);
    throw error;
  }
};

// Heartbeat pulse sent while recorder tab is open (recording OR paused)
export const pulseMultipartUpload = async ({
  teamId,
  folderId,
  fileId,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
}): Promise<void> => {
  if (!fileId) {
    throw new Error('pulseMultipartUpload: missing fileId');
  }

  if (!teamId || !folderId) {
    throw new Error('pulseMultipartUpload: missing teamId or folderId');
  }

  const endpoint = `/files/upload/multipart/pulse/${teamId}/${folderId}/${fileId}`;

  try {
    await apiV2.post(endpoint, undefined, { timeout: TIMEOUT_PULSE });
  } catch (error) {
    // Retry once after 10 s, then log failure
    logger.warn('Pulse endpoint failed, retrying once in 10 s', error);
    await new Promise((resolve) => setTimeout(resolve, 10_000));
    try {
      await apiV2.post(endpoint, undefined, { timeout: TIMEOUT_PULSE });
    } catch (retryError) {
      logger.error('Pulse endpoint failed after retry', retryError);
    }
  }
};

// Pause a multipart upload (called when user explicitly pauses recording)
export const pauseMultipartUpload = async ({
  teamId,
  folderId,
  fileId,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
}): Promise<void> => {
  if (!fileId) {
    throw new Error('pauseMultipartUpload: missing fileId');
  }

  if (!teamId || !folderId) {
    throw new Error('pauseMultipartUpload: missing teamId or folderId');
  }

  const endpoint = `/files/upload/multipart/pause/${teamId}/${folderId}/${fileId}`;

  try {
    await apiV2.post(endpoint, undefined, { timeout: TIMEOUT_API_SHORT });
  } catch (error) {
    logger.error('Pause endpoint failed', error);
    throw error;
  }
};

/**
 * Abort an in-progress multipart upload (called when user cancels upload)
 */
export const abortMultipartUpload = async ({
  teamId,
  folderId,
  fileId,
  uploadId,
  contentType,
  reason,
  reasonCode,
  reasonDetail,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
  uploadId: string;
  contentType: string;
} & MultipartAbortReason): Promise<void> => {
  const endpoint = `/files/upload/multipart/abort/${teamId}/${folderId}/${fileId}/${uploadId}`;

  try {
    await apiV2.post(
      endpoint,
      { contentType, reason, reasonCode, reasonDetail },
      { timeout: TIMEOUT_API_SHORT },
    );
  } catch (error) {
    logger.error('Abort upload endpoint failed', error);
    throw error;
  }
};

/**
 * Best-effort abort for page unload/close paths.
 * Uses keepalive fetch so the request can outlive page teardown.
 */
export const abortMultipartUploadKeepalive = ({
  teamId,
  folderId,
  fileId,
  uploadId,
  contentType,
  reason,
  reasonCode,
  reasonDetail,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
  uploadId: string;
  contentType: string;
} & MultipartAbortReason): void => {
  const baseUrl = apiV2.defaults.baseURL || '';
  const endpoint = `${baseUrl}/files/upload/multipart/abort/${teamId}/${folderId}/${fileId}/${uploadId}`;
  const token = getToken();
  const payload = JSON.stringify({
    contentType,
    reason,
    reasonCode,
    reasonDetail,
  });

  try {
    fetch(endpoint, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: payload,
    }).catch((error) => {
      logger.warn('Keepalive abort request failed', error);
    });
  } catch (error) {
    logger.warn('Failed to send keepalive abort request', error);
  }
};

/**
 * Graceful close for page unload: backend decides whether to finalize
 * (if chunks exist) or abort (if none).
 * Uses keepalive fetch so the request can outlive page teardown.
 */
export const closeMultipartUploadKeepalive = ({
  teamId,
  folderId,
  fileId,
  contentType,
  reason,
  reasonCode,
  reasonDetail,
}: {
  teamId: string;
  folderId: string;
  fileId: string;
  contentType: string;
} & MultipartAbortReason): void => {
  const baseUrl = apiV2.defaults.baseURL || '';
  const endpoint = `${baseUrl}/files/upload/multipart/close/${teamId}/${folderId}/${fileId}`;
  const token = getToken();
  const payload = JSON.stringify({
    contentType,
    reason,
    reasonCode,
    reasonDetail,
  });

  try {
    fetch(endpoint, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: payload,
    }).catch((error) => {
      logger.warn('Keepalive close request failed', error);
    });
  } catch (error) {
    logger.warn('Failed to send keepalive close request', error);
  }
};
